import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { RiskControlAssociations } from '../../risks/components/RiskControlAssociations.tsx';
import { useControlProcessFilterOptions } from '../../../hooks/controls/useControlProcessFilterOptions.ts';
import {
  buildProcessLabelsFromMappingRow,
  buildProcessOptionsFromMappingRow,
  findRiskMappingRowForFormRiskId,
} from '../../../request/controls/controlRiskMappings.ts';
import type { OptionLabel } from '../../../types/options.ts';
import type { ControlRiskAssociationsFormValues } from '../schema.ts';

type ProcessOptionLabel = OptionLabel & {
  processUuid?: string;
  routeProcessId?: string;
  rowId?: string;
};

function registerProcessOptionLabels(
  labels: Record<string, string>,
  option: ProcessOptionLabel
): void {
  const label = String(option.label ?? '').trim();
  if (!label) return;
  const keys = [option.value, option.processUuid, option.routeProcessId, option.rowId]
    .map((key) => String(key ?? '').trim())
    .filter(Boolean);
  for (const key of keys) {
    labels[key] = label;
  }
}

type ControlMappedProcessesFieldProps = {
  riskId: string;
  control: Control<ControlRiskAssociationsFormValues>;
  index: number;
  processLabelsByValue: Record<string, string>;
  riskMappings: Array<Record<string, unknown>>;
  riskOptions?: OptionLabel[];
  readOnly?: boolean;
  onProcessOptionsChange?: (riskId: string, options: ProcessOptionLabel[]) => void;
  auditYearId?: string | null;
};

export const ControlMappedProcessesField: React.FC<ControlMappedProcessesFieldProps> = ({
  riskId,
  control,
  index,
  processLabelsByValue,
  riskMappings,
  riskOptions = [],
  readOnly = false,
  onProcessOptionsChange,
  auditYearId,
}) => {
  const [processSearchInput, setProcessSearchInput] = useState('');
  const fetchPickerOptions = !readOnly;
  const processOptionsQuery = useControlProcessFilterOptions(
    processSearchInput,
    riskId,
    riskMappings,
    fetchPickerOptions,
    riskOptions,
    auditYearId
  );

  const riskMappingRow = useMemo(
    () => findRiskMappingRowForFormRiskId(riskMappings, riskId),
    [riskId, riskMappings]
  );

  const rowProcessLabels = useMemo(() => {
    if (!riskMappingRow) return processLabelsByValue;
    return { ...processLabelsByValue, ...buildProcessLabelsFromMappingRow(riskMappingRow) };
  }, [processLabelsByValue, riskMappingRow]);

  const mappedRowProcessOptions = useMemo(
    () => (riskMappingRow ? buildProcessOptionsFromMappingRow(riskMappingRow) : []),
    [riskMappingRow]
  );

  const processOptions = useMemo(() => {
    const pages = processOptionsQuery.data?.pages ?? [];
    const fromApi = pages.flatMap((page) => page.items) as ProcessOptionLabel[];
    const search = processSearchInput.trim().toLowerCase();
    const matchesSearch = (option: ProcessOptionLabel) => {
      if (!search) return true;
      return (
        option.label.toLowerCase().includes(search) ||
        option.value.toLowerCase().includes(search)
      );
    };

    const merged: ProcessOptionLabel[] = [];
    const seen = new Set<string>();
    const pushOption = (option: ProcessOptionLabel) => {
      const value = String(option.value ?? '').trim();
      if (!value || seen.has(value) || !matchesSearch(option)) return;
      seen.add(value);
      merged.push({
        value,
        label: String(option.label ?? value),
        processUuid: option.processUuid,
        routeProcessId: option.routeProcessId,
        rowId: option.rowId,
      });
    };

    mappedRowProcessOptions.forEach(pushOption);
    fromApi.forEach(pushOption);
    return merged;
  }, [mappedRowProcessOptions, processOptionsQuery.data?.pages, processSearchInput]);

  const wireProcessOptions = useMemo(() => {
    const pages = processOptionsQuery.data?.pages ?? [];
    const fromApi = pages.flatMap((page) => page.items) as ProcessOptionLabel[];
    const merged: ProcessOptionLabel[] = [];
    const seen = new Set<string>();
    const pushOption = (option: ProcessOptionLabel) => {
      const value = String(option.value ?? '').trim();
      if (!value || seen.has(value)) return;
      seen.add(value);
      merged.push({
        value,
        label: String(option.label ?? value),
        processUuid: option.processUuid,
        routeProcessId: option.routeProcessId,
        rowId: option.rowId,
      });
    };
    mappedRowProcessOptions.forEach(pushOption);
    fromApi.forEach(pushOption);
    return merged;
  }, [mappedRowProcessOptions, processOptionsQuery.data?.pages]);

  useEffect(() => {
    onProcessOptionsChange?.(riskId, wireProcessOptions);
  }, [onProcessOptionsChange, riskId, wireProcessOptions]);

  const displayLabelsByValue = useMemo(() => {
    const labels = { ...rowProcessLabels };
    for (const option of mappedRowProcessOptions) {
      registerProcessOptionLabels(labels, option);
    }
    for (const option of processOptions) {
      registerProcessOptionLabels(labels, option as ProcessOptionLabel);
    }
    return labels;
  }, [mappedRowProcessOptions, processOptions, rowProcessLabels]);

  const loadMoreProcesses = useCallback(() => {
    if (processOptionsQuery.hasNextPage && !processOptionsQuery.isFetchingNextPage) {
      void processOptionsQuery.fetchNextPage();
    }
  }, [processOptionsQuery]);

  return (
    <Controller
      control={control}
      name={`riskAssociations.${index}.mitigatingProcessIds`}
      render={({ field, fieldState }) => {
        const selectedValues = field.value ?? [];
        return (
          <div>
            <RiskControlAssociations
              variant="process"
              tagLayout="pills"
              readOnly={readOnly}
              selectedValues={selectedValues}
              controlOptions={processOptions}
              selectedLabelsByValue={displayLabelsByValue}
              loadingOptions={processOptionsQuery.isLoading}
              loadingMoreOptions={processOptionsQuery.isFetchingNextPage}
              searchInputValue={processSearchInput}
              hasMoreOptions={Boolean(processOptionsQuery.hasNextPage)}
              onSearchChange={setProcessSearchInput}
              onLoadMore={loadMoreProcesses}
              sectionTitle="Mapped Processes"
              sectionTitleClassName="text-xs font-semibold text-slate-600"
              sectionHint={readOnly ? 'Mitigating controls linked to this risk.' : 'At least one process is required.'}
              addOptionLabel="Select process..."
              onAdd={(value) => field.onChange([...selectedValues, value])}
              onRemove={(value) => field.onChange(selectedValues.filter((v) => v !== value))}
            />
            {fieldState.error?.message ? (
              <p className="mt-1 text-xs text-rose-600" role="alert">
                {fieldState.error.message}
              </p>
            ) : null}
          </div>
        );
      }}
    />
  );
};
